import { useState, useEffect, useCallback, useRef } from 'react';
import { maintenanceService, MaintenanceStatusDto } from '../services/maintenanceService';

const POLL_INTERVAL_MS = 30 * 1000; // 30 sekunder

export function useMaintenanceCheck() {
    const [status, setStatus] = useState<MaintenanceStatusDto | null>(null);
    const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const checkStatus = useCallback(async () => {
        try {
            const data = await maintenanceService.getStatus();
            setStatus(data);
        } catch {
            // Ignorer feil — prøver igjen ved neste poll
        }
    }, []);

    // Poll status hvert 30. sekund
    useEffect(() => {
        checkStatus();
        const interval = setInterval(checkStatus, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [checkStatus]);

    // Beregn countdown hvert sekund
    useEffect(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (!status?.isScheduled || !status.scheduledAtUtc) {
            setSecondsRemaining(null);
            return;
        }

        const updateCountdown = () => {
            const target = new Date(status.scheduledAtUtc!).getTime();
            const now = Date.now();
            const diff = Math.max(0, Math.floor((target - now) / 1000));
            setSecondsRemaining(diff);
        };

        updateCountdown();
        timerRef.current = setInterval(updateCountdown, 1000);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [status?.isScheduled, status?.scheduledAtUtc]);

    const deactivate = useCallback(async () => {
        try {
            const data = await maintenanceService.deactivate();
            setStatus(data);
        } catch (error) {
            console.error('Feil ved deaktivering av vedlikeholdsmodus:', error);
            throw error;
        }
    }, []);

    return {
        status,
        secondsRemaining,
        deactivate,
        refresh: checkStatus
    };
}
